import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { supabase } from '../lib/supabase';
import FactuurDocument from '../components/pdf/FactuurDocument';
import HerinneringDocument from '../components/pdf/HerinneringDocument';
import { getFactuurPdfLabels, getFactuurStatusLabels, pdfFmtDate } from '../components/pdf/pdfFactuurLabels';
import { escapeHtml } from './escapeHtml';

/**
 * Genereert een PDF blob van een factuur
 * @param {Object} invoice - Factuurobject met client
 * @param {Array} lines - Invoice lines array
 * @param {Object} settings - Bedrijfsinstellingen
 * @param {Object|null} reminderData - Voor herinneringen: { type, newDueDate }
 * @returns {Promise<Blob>}
 */
export async function generateInvoicePDF(invoice, lines, settings, reminderData = null) {
  const Document = reminderData ? HerinneringDocument : FactuurDocument;
  const props = reminderData
    ? { invoice, lines, company: settings, type: reminderData.type, newDueDate: reminderData.newDueDate }
    : { invoice, lines, company: settings, labels: getFactuurPdfLabels(), statusLabels: getFactuurStatusLabels(), fmtDate: pdfFmtDate };

  const doc = React.createElement(Document, props);
  const blob = await pdf(doc).toBlob();
  return blob;
}

/**
 * Upload een factuur-PDF naar Supabase Storage
 * @param {string} invoiceId - UUID van de factuur
 * @param {Blob} pdfBlob - PDF blob
 * @returns {Promise<string>} - Pad van het geüploade bestand
 */
export async function uploadInvoicePDF(invoiceId, pdfBlob) {
  const filePath = `${invoiceId}.pdf`;

  const { data, error } = await supabase.storage
    .from('invoices')
    .upload(filePath, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true,  // Overschrijf bestaande PDF bij herhaalde upload
    });

  if (error) {
    throw new Error(`PDF upload failed: ${error.message}`);
  }

  return filePath;
}

/**
 * Haalt een PDF op uit Storage als base64-encoded string (voor e-mailbijlage)
 * @param {string} invoiceId - UUID van de factuur
 * @returns {Promise<string>} - Base64-encoded PDF
 */
export async function getInvoicePDFBase64(invoiceId) {
  const filePath = `${invoiceId}.pdf`;

  const { data, error } = await supabase.storage
    .from('invoices')
    .download(filePath);

  if (error) {
    throw new Error(`PDF download failed: ${error.message}`);
  }

  // Converteer blob naar base64
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];  // Strip "data:application/pdf;base64,"
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(data);
  });
}

/**
 * Stuurt een factuur per e-mail via de send-email Edge Function
 * @param {Object} invoice - Factuurobject met id en client_id
 * @param {string} subject - E-mail onderwerp
 * @param {string} htmlBody - E-mail body (HTML)
 * @param {string|null} pdfBase64 - Base64-encoded PDF (optioneel)
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export async function sendInvoiceEmail(invoice, subject, htmlBody, pdfBase64 = null, from = null) {
  try {
    // 1. Haal e-mailadres client op via SECURITY DEFINER functie
    const { data: email, error: emailErr } = await supabase.rpc('get_client_email', {
      p_client_id: invoice.client_id
    });

    if (emailErr || !email) {
      throw new Error(`E-mailadres ophalen mislukt: ${emailErr?.message || 'Geen e-mail gevonden'}`);
    }

    // 2. Roep send-email Edge Function aan
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Geen actieve sessie');
    }

    const payload = {
      to: email,
      subject,
      html: htmlBody,
    };

    if (from) {
      payload.from = from;
    }

    if (pdfBase64) {
      payload.attachments = [{
        filename: `factuur-${invoice.invoice_number}.pdf`,
        content: pdfBase64,
      }];
    }

    const response = await fetch(
      `${supabase.supabaseUrl}/functions/v1/send-email`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    return { success: true, messageId: result.messageId };

  } catch (err) {
    console.error('sendInvoiceEmail error:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Zorgt ervoor dat een factuur-PDF bestaat in Storage en geeft base64 terug
 * @param {string} invoiceId - UUID van de factuur
 * @param {Object} invoice - Factuurobject met client
 * @param {Array} lines - Invoice lines array
 * @param {Object} settings - Bedrijfsinstellingen
 * @param {Object|null} reminderData - Voor herinneringen: { type, newDueDate }
 * @returns {Promise<string>} - Base64-encoded PDF
 */
export async function ensureInvoicePDF(invoiceId, invoice, lines, settings, reminderData = null) {
  try {
    // Stap 1: Probeer bestaande PDF op te halen (alleen voor facturen zonder reminderData)
    if (!reminderData) {
      const pdfBase64 = await getInvoicePDFBase64(invoiceId);
      return pdfBase64;
    }
    // Voor herinneringen: altijd vers genereren
    throw new Error('Generate fresh reminder PDF');
  } catch (err) {
    // Stap 2: PDF bestaat niet of is herinnering, genereer
    console.log(`PDF ${reminderData ? 'genereren voor herinnering' : 'niet gevonden'} voor ${invoiceId}, genereren...`);
    const pdfBlob = await generateInvoicePDF(invoice, lines, settings, reminderData);

    // Alleen uploaden naar Storage als het geen herinnering is
    if (!reminderData) {
      await uploadInvoicePDF(invoiceId, pdfBlob);
    }

    // Stap 3: Converteer blob naar base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });
  }
}

/**
 * Vervangt variabelen in een string met echte waarden
 * @param {string} template - Template string met variabelen
 * @param {Object} vars - Object met variabele waarden
 * @returns {string} - String met vervangen variabelen
 */
function replaceVariables(template, vars) {
  return template
    .replace(/\[naam\]/g, vars.naam || '')
    .replace(/\[bedrag\]/g, vars.bedrag || '')
    .replace(/\[factuurnummer\]/g, vars.factuurnummer || '')
    .replace(/\[betaaldatum\]/g, vars.betaaldatum || '')
    .replace(/\[bedrijfsnaam\]/g, vars.bedrijfsnaam || '')
    .replace(/\[betaallink\]/g, vars.betaallink || '')
    .replace(/\[betaalinstructie\]/g, vars.betaalinstructie || '');
}

/**
 * Vraagt (indien nodig) een iDEAL-checkout aan voor deze factuur via de
 * create-invoice-checkout Edge Function. Idempotent: bestaande
 * mollie_checkout_url wordt server-side hergebruikt. Geeft bij falen
 * `null` terug i.p.v. te gooien — de factuurmail moet ALTIJD alsnog
 * verstuurd kunnen worden, ook zonder betaallink (F4-10 Stap 3).
 * @param {string} invoiceId
 * @returns {Promise<string|null>} - mollie_checkout_url of null
 */
export async function ensureInvoiceCheckout(invoiceId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Geen actieve sessie');

    const response = await fetch(
      `${supabase.supabaseUrl}/functions/v1/create-invoice-checkout`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceId }),
      }
    );

    const result = await response.json();
    if (!response.ok) {
      console.error('ensureInvoiceCheckout mislukt:', result.error);
      return null;
    }
    return result.mollie_checkout_url || null;
  } catch (err) {
    console.error('ensureInvoiceCheckout fout:', err.message);
    return null;
  }
}

/**
 * Archiveert (indien aanwezig) de Mollie Payment Link van deze factuur via
 * de archive-invoice-payment-link Edge Function — zodat een klant er niet
 * meer op kan betalen nadat de factuur handmatig is afgehandeld (F4-10
 * kritieke fix, Onderdeel 6). Bewust NIET blokkerend: de statuswijziging/
 * creditnota is dan al gebeurd, een archiveerfout wordt alleen gelogd.
 * @param {string} invoiceId
 * @param {'manual_paid'|'credit_note'} reason
 * @returns {Promise<boolean>} - true als gearchiveerd (of niets te archiveren was)
 */
export async function archiveInvoicePaymentLink(invoiceId, reason) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Geen actieve sessie');

    const response = await fetch(
      `${supabase.supabaseUrl}/functions/v1/archive-invoice-payment-link`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceId, reason }),
      }
    );

    const result = await response.json();
    if (!response.ok) {
      console.error('archiveInvoicePaymentLink mislukt:', result.error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('archiveInvoicePaymentLink fout:', err.message);
    return false;
  }
}

/**
 * Genereert HTML-body voor een factuurmail
 * @param {Object} invoice - Factuurobject
 * @param {Object} settings - Bedrijfsinstellingen (inclusief email_html_factuur, email_aanhef, etc.)
 * @param {Object|null} sepaInfo - Bij SEPA: { ibanLast4, mandateReference } (F4-10 Stap 4c / SEPA-machtigingsaddendum)
 * @param {boolean} idealPaid - Onderdeel 2 (2026-07-27): servertwin-parameter, hier standaard false
 *   (de handmatige "Verstuur factuur"-knop in FacturenPage.jsx verstuurt nooit een al-betaalde
 *   iDEAL-factuur opnieuw) — puur voor structuurgelijkheid met invoice-email.ts.
 * @returns {string} - HTML string
 */
export function generateInvoiceEmailHTML(invoice, settings, checkoutUrl = null, sepaInfo = null, idealPaid = false) {
  const companyName = settings.company_name || 'Fit En Sterker';
  const euro = (val) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(val ?? 0);
  const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Betaallink-knop (alleen bij iDEAL-checkout) — beschikbaar als
  // [betaallink]-variabele voor custom templates, en automatisch
  // toegevoegd in de standaard template hieronder.
  const betaallinkHtml = idealPaid
    ? `<p style="text-align:center;margin:24px 0;"><span style="display:inline-block;background:#dcfce7;color:#166534;padding:12px 24px;border-radius:8px;font-weight:700;">✓ Betaald via iDEAL</span></p>`
    : checkoutUrl
      ? `<p style="text-align:center;margin:24px 0;"><a href="${escapeHtml(checkoutUrl)}" style="display:inline-block;background:#1d9e75;color:white;padding:14px 28px;border-radius:8px;text-decoration:none;font-weight:700;">Betaal nu via iDEAL</a></p>`
      : '';

  // Betaalmethode-bewuste tekst (Onderdeel 2, bugfix): IBAN/overmaken-tekst
  // uitsluitend bij een echt handmatige factuur — voorheen stond dit alleen
  // achter `!sepaInfo`, waardoor een onbetaalde iDEAL-mail zowel de
  // betaalknop als de IBAN-instructie toonde.
  const showManualPaymentInstructions = !sepaInfo && !checkoutUrl && !idealPaid;

  // F4-10 Stap 4c: bij SEPA geen betaallink, maar een tekstblok dat het
  // automatisch-incasseren aankondigt — dit dient tevens als de
  // SEPA-pre-notificatie (de incasso wordt pas op/na de betaaldatum
  // hieronder ingediend, zie process-membership-renewals Taak F).
  const sepaHtml = sepaInfo
    ? `<div style="background:#f0fdf9;border:1px solid #99f6e4;border-radius:8px;padding:15px;margin:20px 0;">
      <p style="margin:0;font-size:14px;color:#374151;">
        Dit bedrag wordt automatisch afgeschreven van uw rekening eindigend op <strong>${escapeHtml(sepaInfo.ibanLast4) || '****'}</strong>, conform de door u getekende SEPA-machtiging.
      </p>
      <p style="margin:8px 0 0;font-size:13px;color:#6b7280;">
        Machtigingskenmerk: <strong>${escapeHtml(sepaInfo.mandateReference) || '[nog niet beschikbaar]'}</strong>
      </p>
      <p style="margin:8px 0 0;font-size:13px;color:#6b7280;">
        De afschrijving wordt op of na de betaaldatum ${fmtDate(invoice.due_date)} ingediend en kan door uw bank tot enkele werkdagen verwerkingstijd vragen. U wordt hierbij via deze factuur vooraf geïnformeerd over de aankomende incasso.
      </p>
    </div>`
    : '';

  // Variabelen voor vervanging. Onderdeel 5 (2026-07-27, Laag-fix —
  // Opus-review, prioritair): naam/bedrijfsnaam/factuurnummer zijn vrije
  // (client- resp. staf-)tekst — escapen vóórdat ze in HTML/
  // replaceVariables terechtkomen. `betaallink` blijft ongewijzigd: dat IS
  // al opgebouwde, vertrouwde HTML-markup.
  const vars = {
    naam: escapeHtml(invoice.client?.full_name || 'klant'),
    bedrag: euro(invoice.total_amount),
    factuurnummer: escapeHtml(invoice.invoice_number || ''),
    betaaldatum: fmtDate(invoice.due_date),
    bedrijfsnaam: escapeHtml(companyName),
    betaallink: betaallinkHtml,
    betaalinstructie: '',
  };

  // Onderdeel 5 (2026-07-27, Laag-fix — Opus-review): betaalmethode-bewuste
  // IBAN/overmaken-instructie (Onderdeel 2) gold voorheen alleen voor de
  // standaardtemplate hieronder — een custom `email_html_factuur` toonde de
  // generieke IBAN-tekst altijd, ongeacht betaalmethode. Nu ook via de
  // `[betaalinstructie]`-placeholder beschikbaar voor custom templates.
  vars.betaalinstructie = showManualPaymentInstructions
    ? `
    <p style="font-size: 14px; color: #666;">
      De factuur vindt u als bijlage bij deze e-mail. Het verschuldigde bedrag kunt u overmaken naar:
    </p>
    <div style="background: #f9fafb; padding: 15px; border-radius: 4px; margin: 15px 0;">
      <p style="margin: 0; font-size: 13px; color: #666;"><strong>IBAN:</strong> ${escapeHtml(settings.company_iban) || '—'}</p>
      <p style="margin: 8px 0 0; font-size: 13px; color: #666;"><strong>t.n.v.:</strong> ${vars.bedrijfsnaam}</p>
      <p style="margin: 8px 0 0; font-size: 13px; color: #666;"><strong>Onder vermelding van:</strong> ${vars.factuurnummer}</p>
    </div>`
    : `
    <p style="font-size: 14px; color: #666;">
      De factuur vindt u als bijlage bij deze e-mail.
    </p>`;

  // Prioriteit 1: Custom HTML template
  if (settings.email_html_factuur && settings.email_html_factuur.trim()) {
    return replaceVariables(settings.email_html_factuur, vars);
  }

  // Prioriteit 2: Standaard template met instellingen
  const aanhef = settings.email_aanhef
    ? replaceVariables(settings.email_aanhef, vars)
    : `Beste ${vars.naam},`;

  const intro = settings.email_intro_factuur
    ? replaceVariables(settings.email_intro_factuur, vars)
    : `Hierbij ontvangt u factuur <strong>${vars.factuurnummer}</strong> van ${vars.bedrijfsnaam}.`;

  const slotgroet = settings.email_slotgroet
    ? replaceVariables(settings.email_slotgroet, vars)
    : `Met vriendelijke groet,<br><strong>${vars.bedrijfsnaam}</strong>`;

  return `
<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Factuur ${vars.factuurnummer}</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: linear-gradient(135deg, #1d9e75 0%, #1a5c3a 100%); color: white; padding: 30px 20px; border-radius: 8px 8px 0 0; text-align: center;">
    <h1 style="margin: 0; font-size: 24px;">Nieuwe factuur</h1>
  </div>

  <div style="background: white; padding: 30px 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
    <p style="font-size: 16px; margin-top: 0;">${aanhef}</p>

    <p style="font-size: 14px; color: #666;">
      ${intro}
    </p>

    <div style="background: #f9fafb; border-left: 4px solid #1d9e75; padding: 15px; margin: 20px 0; border-radius: 4px;">
      <p style="margin: 0 0 8px; font-size: 14px; color: #666;">Totaalbedrag:</p>
      <p style="margin: 0; font-size: 24px; font-weight: 700; color: #1d9e75;">${euro(invoice.total_amount)}</p>
    </div>

    ${betaallinkHtml}

    ${sepaHtml}

    ${(invoice.due_date && !idealPaid) ? `
    <p style="font-size: 14px; color: #666; margin-top: 20px;">
      <strong>Uiterste betaaldatum:</strong> ${fmtDate(invoice.due_date)}
    </p>
    ` : ''}

    ${vars.betaalinstructie}

    <p style="font-size: 14px; color: #666; margin-top: 20px;">
      ${slotgroet}
    </p>
  </div>

  <div style="text-align: center; padding: 20px; font-size: 12px; color: #999;">
    <p style="margin: 0;">Deze e-mail is automatisch gegenereerd via het Fit En Sterker portaal.</p>
  </div>
</body>
</html>
  `.trim();
}

/**
 * Genereert HTML-body voor een herinneringsmail
 * @param {Object} invoice - Factuurobject
 * @param {Object} settings - Bedrijfsinstellingen (inclusief email_html_*, email_aanhef, etc.)
 * @param {string} type - 'reminder' | 'final_notice' | 'collections'
 * @returns {string} - HTML string
 */
export function generateReminderEmailHTML(invoice, settings, type) {
  const companyName = settings.company_name || 'Fit En Sterker';
  const euro = (val) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(val ?? 0);
  const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Variabelen voor vervanging (zie toelichting escaping bij
  // generateInvoiceEmailHTML hierboven).
  const vars = {
    naam: escapeHtml(invoice.client?.full_name || 'klant'),
    bedrag: euro(invoice.total_amount),
    factuurnummer: escapeHtml(invoice.invoice_number || ''),
    betaaldatum: fmtDate(invoice.due_date),
    bedrijfsnaam: escapeHtml(companyName),
  };

  // Map type naar settings key
  const typeMap = {
    reminder: 'herinnering',
    final_notice: 'aanmaning',
    collections: 'incasso',
  };
  const settingsKey = typeMap[type] || 'herinnering';

  // Prioriteit 1: Custom HTML template
  const customHtmlKey = `email_html_${settingsKey}`;
  if (settings[customHtmlKey] && settings[customHtmlKey].trim()) {
    return replaceVariables(settings[customHtmlKey], vars);
  }

  // Prioriteit 2: Standaard template met instellingen
  const aanhef = settings.email_aanhef
    ? replaceVariables(settings.email_aanhef, vars)
    : `Beste ${vars.naam},`;

  const introKey = `email_intro_${settingsKey}`;
  const defaultIntros = {
    reminder: `Volgens onze administratie hebben we nog geen betaling ontvangen voor factuur <strong>${vars.factuurnummer}</strong>. Mocht u deze factuur al hebben voldaan, dan kunt u deze herinnering als niet verzonden beschouwen.`,
    final_notice: `Dit is de laatste aanmaning voor factuur <strong>${vars.factuurnummer}</strong>. Indien we binnen 7 dagen geen betaling ontvangen, zijn wij genoodzaakt deze zaak over te dragen aan een incassobureau, met bijkomende kosten voor u.`,
    collections: `Factuur <strong>${vars.factuurnummer}</strong> is overgedragen aan een incassobureau wegens uitblijvende betaling. U ontvangt binnenkort bericht van het incassobureau. Tevens is uw account bevroren totdat deze kwestie is opgelost.`,
  };
  const intro = settings[introKey]
    ? replaceVariables(settings[introKey], vars)
    : defaultIntros[type];

  const slotgroet = settings.email_slotgroet
    ? replaceVariables(settings.email_slotgroet, vars)
    : `Met vriendelijke groet,<br><strong>${vars.bedrijfsnaam}</strong>`;

  const titles = {
    reminder: 'Betalingsherinnering',
    final_notice: 'Laatste aanmaning',
    collections: 'Overdracht incassobureau'
  };

  const colors = {
    reminder: '#f59e0b',
    final_notice: '#dc2626',
    collections: '#991b1b'
  };

  return `
<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${titles[type]}</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background: ${colors[type]}; color: white; padding: 30px 20px; border-radius: 8px 8px 0 0; text-align: center;">
    <h1 style="margin: 0; font-size: 24px;">${titles[type]}</h1>
  </div>

  <div style="background: white; padding: 30px 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
    <p style="font-size: 16px; margin-top: 0;">${aanhef}</p>

    <p style="font-size: 14px; color: #666;">
      ${intro}
    </p>

    <div style="background: #f9fafb; border-left: 4px solid ${colors[type]}; padding: 15px; margin: 20px 0; border-radius: 4px;">
      <p style="margin: 0 0 8px; font-size: 14px; color: #666;">Openstaand bedrag:</p>
      <p style="margin: 0; font-size: 24px; font-weight: 700; color: ${colors[type]};">${euro(invoice.total_amount)}</p>
      ${invoice.due_date ? `<p style="margin: 8px 0 0; font-size: 13px; color: #666;"><strong>Uiterste betaaldatum:</strong> ${fmtDate(invoice.due_date)}</p>` : ''}
    </div>

    ${type !== 'collections' ? `
    <p style="font-size: 14px; color: #666;">
      U kunt het openstaande bedrag overmaken naar:
    </p>

    <div style="background: #f9fafb; padding: 15px; border-radius: 4px; margin: 15px 0;">
      <p style="margin: 0; font-size: 13px; color: #666;"><strong>IBAN:</strong> ${escapeHtml(settings.company_iban) || '—'}</p>
      <p style="margin: 8px 0 0; font-size: 13px; color: #666;"><strong>t.n.v.:</strong> ${vars.bedrijfsnaam}</p>
      <p style="margin: 8px 0 0; font-size: 13px; color: #666;"><strong>Onder vermelding van:</strong> ${vars.factuurnummer}</p>
    </div>

    <p style="font-size: 14px; color: #666;">
      Voor vragen kunt u contact met ons opnemen via ${escapeHtml(settings.company_email || settings.company_phone) || 'onze contactgegevens'}.
    </p>
    ` : `
    <p style="font-size: 14px; color: #666;">
      Voor vragen over deze overdracht kunt u contact opnemen met ons incassobureau. De contactgegevens ontvangt u in hun brief.
    </p>
    `}

    <p style="font-size: 14px; color: #666; margin-top: 20px;">
      ${slotgroet}
    </p>
  </div>

  <div style="text-align: center; padding: 20px; font-size: 12px; color: #999;">
    <p style="margin: 0;">Deze e-mail is automatisch gegenereerd via het Fit En Sterker portaal.</p>
  </div>
</body>
</html>
  `.trim();
}
